import React, { Suspense, useEffect, useMemo, useRef } from 'react';
import { Environment, useTexture } from '@react-three/drei';
import { useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { resolveExteriorPack } from '../../data/labExteriorCatalog';

/**
 * Exterior environment stack around the hyperlocal ADU.
 * Docs: docs/SIM_LAB_EXTERIOR_ENV.md
 *
 * Phase 1 look: ground-projected HDRI (photo becomes ground + horizon,
 * like the classic "car on HDRI" shot) + a sand apron under the ADU that
 * hides the projection smear at the tripod nadir.
 *
 * @param {object} props
 * @param {string} props.exteriorId - catalog env id (`beach` | `none` | …)
 * @param {{ sky?: boolean, ground?: boolean }} [props.layers]
 * @param {{ floorY?: number, center?: number[], span?: number }} [props.localize]
 */
export default function LabExterior({
  exteriorId = 'none',
  /** Optional sky variant asset id (must be in the env's variant list). */
  skyAssetId = null,
  layers = { sky: true, ground: true },
  localize = null,
  /** Motion FX only run in walk mode outside the structure. */
  walkMode = false,
}) {
  const pack = useMemo(
    () => resolveExteriorPack(exteriorId, { skyAssetId }),
    [exteriorId, skyAssetId]
  );
  if (!pack || exteriorId === 'none') return null;

  return (
    <ExteriorErrorBoundary resetKey={`${exteriorId}:${skyAssetId || ''}`}>
      <Suspense fallback={null}>
        <LabExteriorInner
          pack={pack}
          layers={layers}
          localize={localize}
          walkMode={walkMode}
        />
      </Suspense>
    </ExteriorErrorBoundary>
  );
}

/**
 * A failed HDR/texture fetch (404 → SPA index.html → RGBELoader "no header")
 * must degrade to the plain lab scene — never crash the canvas / WebGL
 * context. Docs: docs/SIM_LAB_EXTERIOR_ENV.md
 */
class ExteriorErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error) {
    console.warn('Lab exterior failed to load — falling back to lab scene.', error);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.resetKey !== this.props.resetKey && this.state.failed) {
      this.setState({ failed: false });
    }
  }

  render() {
    if (this.state.failed) return null;
    return this.props.children;
  }
}

/** HDRI capture eye height (Poly Haven tripod ≈ 1.8 m) — ground projection param. */
const HDRI_CAPTURE_HEIGHT_M = 1.8;

function LabExteriorInner({ pack, layers, localize, walkMode }) {
  const env = pack.env;
  const floorY = Number.isFinite(localize?.floorY) ? localize.floorY : 0;
  const cx = Number(localize?.center?.[0]) || 0;
  const cz = Number(localize?.center?.[2]) || 0;
  const span = Math.max(8, Number(localize?.span) || 20);
  const clearance = span * 0.5 + (Number(env?.clearanceMarginM) || 2);
  const groundRadius = Math.max(40, Number(env?.groundRadiusM) || 80);

  return (
    <group name="lab-exterior" position={[cx, floorY, cz]}>
      {layers.sky !== false && pack.sky?.url ? (
        <Environment
          files={pack.sky.url}
          ground={{
            height: HDRI_CAPTURE_HEIGHT_M,
            radius: groundRadius,
            scale: groundRadius * 4,
          }}
          resolution={256}
        />
      ) : null}

      {layers.ground !== false && pack.ground ? (
        <SandApron asset={pack.ground} radius={clearance + 6} />
      ) : null}

      <ExteriorMotionFx
        walkMode={walkMode}
        footprint={localize?.footprint}
        innerRadius={clearance + 7}
        outerRadius={groundRadius * 0.95}
      />
    </group>
  );
}

/* ————— Motion FX — docs/SIM_LAB_EXTERIOR_ENV.md § Motion FX —————
   Very subtle water shimmer + cloud drift. Active only while walking
   outside the structure footprint; fades in/out and culls when hidden. */

const WATER_MAX_OPACITY = 0.12;
const CLOUD_MAX_OPACITY = 0.16;
const FADE_PER_SEC = 2.2;
const FOOTPRINT_MARGIN_M = 0.4;

/** Soft-blob noise texture (procedural — no asset download). */
function makeNoiseTexture(size = 256, blobs = 42, seed = 7) {
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = size;
  const ctx = canvas.getContext('2d');
  ctx.clearRect(0, 0, size, size);
  let s = seed;
  const rand = () => {
    // Deterministic LCG so both FX layers look stable between mounts.
    s = (s * 1664525 + 1013904223) % 4294967296;
    return s / 4294967296;
  };
  for (let i = 0; i < blobs; i += 1) {
    const x = rand() * size;
    const y = rand() * size;
    const r = size * (0.05 + rand() * 0.16);
    const a = 0.04 + rand() * 0.1;
    const g = ctx.createRadialGradient(x, y, 0, x, y, r);
    g.addColorStop(0, `rgba(255,255,255,${a})`);
    g.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, size, size);
  }
  const tex = new THREE.CanvasTexture(canvas);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  return tex;
}

function ExteriorMotionFx({ walkMode, footprint, innerRadius, outerRadius }) {
  const { camera } = useThree();
  const groupRef = useRef(null);
  const waterMatRef = useRef(null);
  const cloudMatRef = useRef(null);
  const fadeRef = useRef(0);

  const waterTex = useMemo(() => {
    const t = makeNoiseTexture(256, 48, 11);
    t.repeat.set(6, 6);
    return t;
  }, []);
  const cloudTex = useMemo(() => {
    const t = makeNoiseTexture(256, 26, 29);
    t.repeat.set(3, 3);
    return t;
  }, []);
  useEffect(
    () => () => {
      waterTex.dispose();
      cloudTex.dispose();
    },
    [waterTex, cloudTex]
  );

  const waterGeo = useMemo(
    () =>
      new THREE.RingGeometry(
        Math.max(2, innerRadius),
        Math.max(innerRadius + 6, outerRadius),
        72
      ),
    [innerRadius, outerRadius]
  );

  useFrame((_, dt) => {
    const group = groupRef.current;
    if (!group) return;

    // Outside test in world XZ against the localize footprint rect.
    let outside = true;
    if (footprint) {
      const mx = (Number(footprint.width) || 0) / 2 + FOOTPRINT_MARGIN_M;
      const mz = (Number(footprint.depth) || 0) / 2 + FOOTPRINT_MARGIN_M;
      outside =
        Math.abs(camera.position.x - (Number(footprint.cx) || 0)) > mx ||
        Math.abs(camera.position.z - (Number(footprint.cz) || 0)) > mz;
    }
    const target = walkMode && outside ? 1 : 0;

    const step = FADE_PER_SEC * Math.min(dt || 0.016, 0.1);
    fadeRef.current =
      target > fadeRef.current
        ? Math.min(target, fadeRef.current + step)
        : Math.max(target, fadeRef.current - step);

    const fade = fadeRef.current;
    group.visible = fade > 0.01;
    if (!group.visible) return;

    const t = performance.now() / 1000;
    if (waterMatRef.current) {
      // Slow roll toward shore + a faint swell pulse.
      waterTex.offset.set((t * 0.006) % 1, (t * 0.0035) % 1);
      waterMatRef.current.opacity =
        fade * WATER_MAX_OPACITY * (0.8 + 0.2 * Math.sin(t * 0.6));
    }
    if (cloudMatRef.current) {
      cloudTex.offset.set((t * 0.0022) % 1, (t * 0.0009) % 1);
      cloudMatRef.current.opacity = fade * CLOUD_MAX_OPACITY;
    }
  });

  return (
    <group ref={groupRef} visible={false}>
      {/* Water shimmer band over the HDRI midground */}
      <mesh
        rotation={[-Math.PI / 2, 0, 0]}
        position={[0, 0.06, 0]}
        geometry={waterGeo}
        userData={{ labWalkIgnore: true, labExterior: true, annotateIgnore: true }}
      >
        <meshBasicMaterial
          ref={waterMatRef}
          map={waterTex}
          alphaMap={waterTex}
          color="#cfe6ee"
          transparent
          opacity={0}
          depthWrite={false}
          side={THREE.DoubleSide}
        />
      </mesh>

      {/* High cloud wisps drifting overhead */}
      <mesh
        rotation={[-Math.PI / 2, 0, 0]}
        position={[0, 48, 0]}
        userData={{ labWalkIgnore: true, labExterior: true, annotateIgnore: true }}
      >
        <circleGeometry args={[outerRadius * 2.4, 48]} />
        <meshBasicMaterial
          ref={cloudMatRef}
          map={cloudTex}
          alphaMap={cloudTex}
          color="#ffffff"
          transparent
          opacity={0}
          depthWrite={false}
          side={THREE.DoubleSide}
        />
      </mesh>
    </group>
  );
}

/**
 * Sand disk under / around the ADU — anchors the model on the projected
 * photo ground and covers the stretched pixels at the HDRI nadir.
 */
function SandApron({ asset, radius }) {
  const maps = asset.maps || {};
  const diffUrl = maps.diffuse || asset.url;
  const norUrl = maps.normal || diffUrl;
  const roughUrl = maps.rough || diffUrl;
  const [diff, nor, rough] = useTexture([diffUrl, norUrl, roughUrl]);

  useEffect(() => {
    const tiles = Math.max(2, radius / 5);
    [diff, nor, rough].forEach((tex, i) => {
      if (!tex) return;
      tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
      tex.repeat.set(tiles, tiles);
      tex.colorSpace = i === 0 ? THREE.SRGBColorSpace : THREE.NoColorSpace;
      tex.anisotropy = 8;
      tex.needsUpdate = true;
    });
  }, [diff, nor, rough, radius]);

  return (
    <mesh
      rotation={[-Math.PI / 2, 0, 0]}
      position={[0, -0.015, 0]}
      receiveShadow
      userData={{ labWalkIgnore: true, labExterior: true }}
    >
      <circleGeometry args={[radius, 72]} />
      <meshStandardMaterial
        map={diff}
        normalMap={nor}
        roughnessMap={rough}
        roughness={1}
        metalness={0}
      />
    </mesh>
  );
}
